/** Shared grid metrics for virtual schedule canvas (must match DnDSchedule/Schedule.tsx). */
export const SCHEDULE_GRID_TIME_COLUMN_WIDTH = 75;
export const SCHEDULE_GRID_HEADER_HEIGHT = 36;
export const SCHEDULE_GRID_FREE_SLOT_MINUTES = 5;
export const SCHEDULE_GRID_MAX_COURT_GAP_PX = 5;

export const SCHEDULE_ZOOM_SCALE_MIN = 0.25;
export const SCHEDULE_ZOOM_SCALE_STEP = 0.25;
export const SCHEDULE_ZOOM_SCALE_MAX = 20;
export const SCHEDULE_ZOOM_COLUMN_WIDTH_MIN = 20;
export const SCHEDULE_ZOOM_COLUMN_WIDTH_MAX = 400;

/** Tolerance for subpixel row layout and browser zoom (e.g. 125%). */
export const SCHEDULE_VIEWPORT_FILL_HEIGHT_SLACK_PX = 2;

export type ScheduleZoomFloors = {
  /** Scale that fills the viewport height (anti-void bump), not the slider minimum. */
  minScale: number;
  /** Smallest width that fills the viewport (anti-void bump), not the slider minimum. */
  minColumnWidth: number;
  viewportWidth: number;
  viewportHeight: number;
  courtCount: number;
  slotCount: number;
};

export const areScheduleZoomFloorsEqual = (
  a: ScheduleZoomFloors,
  b: ScheduleZoomFloors
) =>
  a.minScale === b.minScale &&
  a.minColumnWidth === b.minColumnWidth &&
  a.viewportWidth === b.viewportWidth &&
  a.viewportHeight === b.viewportHeight &&
  a.courtCount === b.courtCount &&
  a.slotCount === b.slotCount;

/** Space between virtual court columns; scales down with column width (matches Schedule.tsx). */
export const getScheduleCourtGapPx = (courtWidth: number) =>
  Math.max(
    0,
    Math.min(SCHEDULE_GRID_MAX_COURT_GAP_PX, Math.floor(courtWidth / 45))
  );

export const getScheduleSlotCount = (dayStartMs: number, dayEndMs: number) =>
  Math.max(
    1,
    Math.ceil(
      (dayEndMs - dayStartMs) /
        (SCHEDULE_GRID_FREE_SLOT_MINUTES * 60_000)
    )
  );

export const getScheduleRowHeightPx = (scale: number) =>
  SCHEDULE_GRID_FREE_SLOT_MINUTES * scale;

/**
 * Row height as actually laid out. Fractional scales (e.g. 0.25 → 1.25px rows) often
 * measure shorter than nominal size, which leaves a void below the last time slot.
 */
export const getScheduleLaidOutRowHeightPx = (scale: number) => {
  const rowHeight = getScheduleRowHeightPx(scale);
  return rowHeight % 1 === 0 ? rowHeight : Math.max(1, Math.floor(rowHeight));
};

export const getScheduleCanvasHeightPx = (slotCount: number, scale: number) =>
  SCHEDULE_GRID_HEADER_HEIGHT + slotCount * getScheduleLaidOutRowHeightPx(scale);

export const scheduleScaleFillsViewportHeight = (
  slotCount: number,
  scale: number,
  viewportHeight: number
) =>
  getScheduleCanvasHeightPx(slotCount, scale) >=
  viewportHeight - SCHEDULE_VIEWPORT_FILL_HEIGHT_SLACK_PX;

export const getScheduleCanvasWidthPx = (courtCount: number, courtWidth: number) => {
  const gap = getScheduleCourtGapPx(courtWidth);
  return (
    SCHEDULE_GRID_TIME_COLUMN_WIDTH +
    courtCount * (courtWidth + gap)
  );
};

/**
 * Column width for grid layout when content is narrower than the viewport.
 * Bumps width so courts reach the right edge instead of leaving empty grey past the last court.
 * Redux `courtWidth` may stay lower (e.g. 20) for busy days; display uses the bumped value here.
 */
export const getScheduleEffectiveCourtWidthPx = (
  courtCount: number,
  courtWidth: number,
  viewportWidth: number
): number => {
  if (viewportWidth <= 0 || courtCount <= 0) return courtWidth;

  const contentWidth = getScheduleCanvasWidthPx(courtCount, courtWidth);
  if (contentWidth >= viewportWidth) return courtWidth;

  const fillWidth = getMinColumnWidthForViewport(courtCount, viewportWidth);
  return Math.min(
    SCHEDULE_ZOOM_COLUMN_WIDTH_MAX,
    Math.max(courtWidth, fillWidth)
  );
};

export const roundScheduleScale = (value: number) =>
  Math.round(value / SCHEDULE_ZOOM_SCALE_STEP) * SCHEDULE_ZOOM_SCALE_STEP;

export const ceilScheduleScale = (value: number) =>
  Math.ceil(value / SCHEDULE_ZOOM_SCALE_STEP) * SCHEDULE_ZOOM_SCALE_STEP;

/**
 * Smallest scale (px per minute) so day canvas height fills the scroll viewport.
 * Capped at SCHEDULE_ZOOM_SCALE_MAX; may stay at SCHEDULE_ZOOM_SCALE_MIN when content already overflows.
 */
export const getMinScaleForViewport = (
  slotCount: number,
  viewportHeight: number
): number => {
  if (viewportHeight <= 0 || slotCount <= 0) return SCHEDULE_ZOOM_SCALE_MIN;

  if (scheduleScaleFillsViewportHeight(slotCount, SCHEDULE_ZOOM_SCALE_MIN, viewportHeight)) {
    return SCHEDULE_ZOOM_SCALE_MIN;
  }

  let scale = SCHEDULE_ZOOM_SCALE_MIN;
  while (
    scale < SCHEDULE_ZOOM_SCALE_MAX &&
    !scheduleScaleFillsViewportHeight(slotCount, scale, viewportHeight)
  ) {
    scale += SCHEDULE_ZOOM_SCALE_STEP;
  }
  return roundScheduleScale(Math.min(scale, SCHEDULE_ZOOM_SCALE_MAX));
};

/**
 * Smallest column width so day canvas width fills the scroll viewport.
 * When courts cannot fit even at max width, returns SCHEDULE_ZOOM_COLUMN_WIDTH_MIN (horizontal scroll).
 */
export const getMinColumnWidthForViewport = (
  courtCount: number,
  viewportWidth: number
): number => {
  if (viewportWidth <= 0 || courtCount <= 0) return SCHEDULE_ZOOM_COLUMN_WIDTH_MIN;

  const widthAtMax = getScheduleCanvasWidthPx(courtCount, SCHEDULE_ZOOM_COLUMN_WIDTH_MAX);
  if (widthAtMax <= viewportWidth) return SCHEDULE_ZOOM_COLUMN_WIDTH_MAX;

  const widthAtMin = getScheduleCanvasWidthPx(courtCount, SCHEDULE_ZOOM_COLUMN_WIDTH_MIN);
  if (widthAtMin >= viewportWidth) return SCHEDULE_ZOOM_COLUMN_WIDTH_MIN;

  let lo = SCHEDULE_ZOOM_COLUMN_WIDTH_MIN;
  let hi = SCHEDULE_ZOOM_COLUMN_WIDTH_MAX;
  while (lo < hi) {
    const mid = Math.floor((lo + hi) / 2);
    if (getScheduleCanvasWidthPx(courtCount, mid) >= viewportWidth) {
      hi = mid;
    } else {
      lo = mid + 1;
    }
  }
  return lo;
};

export const mergeScheduleZoomFloors = (
  current: ScheduleZoomFloors,
  next: ScheduleZoomFloors
): ScheduleZoomFloors => {
  const courtCount =
    current.courtCount > 0 && next.courtCount > 0
      ? Math.min(current.courtCount, next.courtCount)
      : Math.max(current.courtCount, next.courtCount);
  const slotCount = Math.max(current.slotCount, next.slotCount);
  return {
    minScale: Math.max(current.minScale, next.minScale),
    minColumnWidth: Math.max(current.minColumnWidth, next.minColumnWidth),
    viewportWidth: Math.max(current.viewportWidth, next.viewportWidth),
    viewportHeight: Math.max(current.viewportHeight, next.viewportHeight),
    courtCount,
    slotCount,
  };
};

/** Clamps scale to absolute bounds. Viewport-fill (`floors.minScale`) is not a slider floor. */
export const clampScheduleScaleWithFloors = (
  value: number,
  floors: ScheduleZoomFloors,
  options?: { bumpToViewportFill?: boolean }
) => {
  const clamped = Math.min(
    SCHEDULE_ZOOM_SCALE_MAX,
    Math.max(SCHEDULE_ZOOM_SCALE_MIN, roundScheduleScale(value))
  );
  if (!options?.bumpToViewportFill) return clamped;

  const { slotCount, viewportHeight, minScale } = floors;
  if (
    viewportHeight <= 0 ||
    slotCount <= 0 ||
    scheduleScaleFillsViewportHeight(slotCount, clamped, viewportHeight)
  ) {
    return clamped;
  }

  let filled = Math.max(clamped, roundScheduleScale(minScale));
  while (
    filled < SCHEDULE_ZOOM_SCALE_MAX &&
    !scheduleScaleFillsViewportHeight(slotCount, filled, viewportHeight)
  ) {
    filled += SCHEDULE_ZOOM_SCALE_STEP;
  }
  return Math.min(SCHEDULE_ZOOM_SCALE_MAX, roundScheduleScale(filled));
};

/** Clamps column width to absolute bounds. Viewport-fill (`floors.minColumnWidth`) is not a slider floor. */
export const clampScheduleColumnWidthWithFloors = (
  value: number,
  floors: ScheduleZoomFloors,
  options?: { bumpToViewportFill?: boolean }
) => {
  const clamped = Math.min(
    SCHEDULE_ZOOM_COLUMN_WIDTH_MAX,
    Math.max(SCHEDULE_ZOOM_COLUMN_WIDTH_MIN, value)
  );
  if (!options?.bumpToViewportFill) return clamped;

  const { courtCount, viewportWidth, minColumnWidth } = floors;
  if (
    viewportWidth <= 0 ||
    courtCount <= 0 ||
    getScheduleCanvasWidthPx(courtCount, clamped) >= viewportWidth
  ) {
    return clamped;
  }
  return Math.min(
    SCHEDULE_ZOOM_COLUMN_WIDTH_MAX,
    Math.max(clamped, minColumnWidth)
  );
};

/** Prevent scroll offset past virtual canvas (empty grey/white past grid). */
export const clampScheduleGridScroll = (
  scrollEl: HTMLElement,
  canvasWidth: number,
  canvasHeight: number
) => {
  const maxLeft = Math.max(0, canvasWidth - scrollEl.clientWidth);
  const maxTop = Math.max(0, canvasHeight - scrollEl.clientHeight);
  if (scrollEl.scrollLeft > maxLeft) scrollEl.scrollLeft = maxLeft;
  if (scrollEl.scrollTop > maxTop) scrollEl.scrollTop = maxTop;
};

export const computeScheduleZoomFloorsForDay = (
  slotCount: number,
  courtCount: number,
  viewportWidth: number,
  viewportHeight: number
): ScheduleZoomFloors => ({
  minScale: getMinScaleForViewport(slotCount, viewportHeight),
  minColumnWidth: getMinColumnWidthForViewport(courtCount, viewportWidth),
  viewportWidth,
  viewportHeight,
  courtCount,
  slotCount,
});
